import * as m from "mithril";
import './BootstrapForm.css';
import { InternationalStringDTO } from "../apiclient/ApiDtos";

type InputType = "text" | "number" | "email" | "tel";

interface InputAttrs {
    id?: string,
    name: string,
    type?: InputType,
    required?: boolean,
    pattern?: string,
    initialValue?: string | null,
    value?: string | null,
    placeholder?: string,
    maxlength?: number,
    inputmode?: InputType,
    title?: string,
    lang: string,
    validityReportingAllowed?: boolean,
    getCustomValidityMessage?: (validityState: ValidityState) => InternationalStringDTO | null,
    onfocus?: (str: string) => void,
    onValueChange: (newValue: string) => void,
    onInvalid: (vs: ValidityState) => void
}

interface LabelAttrs {
    label: string,
    required?: boolean,
    optional?: boolean,
    for?: string,
}

interface InputGroupAttrs extends InputAttrs, LabelAttrs {
    instruction?: string,
}

export interface Option {
    label: string;
    value: string;
    description?: string;
}

interface RadioGroupAttrs extends LabelAttrs {
    name: string,
    lang: string,
    options: Option[],
    initialValue?: string | null,
    layout?: "horizontal" | "vertical",
    useBackground?: boolean,
    //title?:string, // Title does not work as custom validation message in radio input
    validityReportingAllowed?: boolean,
    getCustomValidityMessage?: (validityState: ValidityState) => InternationalStringDTO | null,
    onValueChange: (newValue: string) => void,
    onInvalid: (vs: ValidityState) => void
}

interface ValidationReporting { visible: boolean, message: InternationalStringDTO | null }

function Label(): m.Component<LabelAttrs> {
    return {
        view(vnode) {
            return m("label.d-flex.flex-row.justify-content-between", {
                for: vnode.attrs.for,
            }, [

                m('div', [
                    vnode.attrs.label,
                    (vnode.attrs.required) ? m('sup.ml-1', { class: "error" }, '*') : null
                ]),

                (vnode.attrs.optional) ?
                    m('div', [

                        m('small', { class: "ml-auto text-muted" }, 'optional')

                    ]) : null,

            ])
        }
    }
}

export function InputGroup(): m.Component<InputGroupAttrs> {
    let validationReporting: ValidationReporting = {
        visible: false,
        message: null
    };

    return {
        oncreate(vnode) {
            let fieldElem = vnode.dom.querySelector("input");
            if (fieldElem) {
                if (vnode.attrs.initialValue) {
                    fieldElem.setAttribute('value', vnode.attrs.initialValue);
                }
            }

        },

        view(vnode) {
            let inputId = vnode.attrs.id || "input-id-" + vnode.attrs.name;
            let inputType = vnode.attrs.type || "text";
            let isRequired = vnode.attrs.required || false;
            let inputValue = vnode.attrs.value || null;
            let placeholder = vnode.attrs.placeholder || null;

            let pattern = vnode.attrs.pattern || null;
            //let patternWrapper = (pattern && patternValidationEnabled)? {pattern: pattern} : {};

            return m("div.form-group", { style: "position: relative;" },
                [
                    m(Label, {
                        label: vnode.attrs.label,
                        required: isRequired,
                        optional: vnode.attrs.optional,
                        for: inputId
                    }),

                    m("input", {
                        id: inputId,
                        name: vnode.attrs.name,
                        value: inputValue,
                        type: inputType,
                        class: "form-control",
                        required: isRequired,
                        pattern: pattern,
                        placeholder: placeholder,
                        maxlength: vnode.attrs.maxlength,
                        inputmode: vnode.attrs.inputmode,
                        onfocus: (event) => {
                            inputValue = event.target.value;
                            //vnode.attrs.onfocus(inputValue);   

                            validationReporting.visible = false;
                        },
                        onblur: (event) => {

                        },
                        onchange: (event) => {
                            // console.log("new value : " + event.target.value);
                            inputValue = event.target.value;
                            if (inputValue) {
                                if (vnode.attrs.inputmode == 'number') {
                                    inputValue = inputValue.replace(/\D/g, "");
                                    event.target.value = inputValue;
                                }
                                vnode.attrs.onValueChange(inputValue);
                            }

                            validationReporting.visible = false;
                        },
                        oninvalid: (event) => {

                            //console.log("Invalid ", event);
                            //event.target.setCustomValidity('Hello')
                            //https://davidwalsh.name/html5-validation
                            if (event.target && vnode.attrs.getCustomValidityMessage) {
                                //event.preventDefault();
                                if (vnode.attrs.name == 'name') {
                                    console.log('input:' + vnode.attrs.name, event.target.validity);
                                }
                                //let validity:ValidityState = event.target.validity;  
                                let message = vnode.attrs.getCustomValidityMessage(event.target.validity);
                                //event.target.setCustomValidity(message);     
                                validationReporting.visible = true;
                                validationReporting.message = message;

                                vnode.attrs.onInvalid(event.target.validity);
                            }
                        },
                        oninput: (event) => {
                            if (vnode.attrs.getCustomValidityMessage) {
                                //event.target.setCustomValidity('');   
                                validationReporting.visible = false;
                            }
                        }
                    }),

                    (vnode.attrs.validityReportingAllowed && validationReporting.visible) ?
                        m("div.validation-popup",
                            m("span.validation-message",
                                m("span.text-secondary",
                                    (vnode.attrs.lang == 'en') ? validationReporting.message?.en : validationReporting.message?.my)
                            )
                        ) : null,

                    (vnode.attrs.instruction != null) ?
                        m("small", {
                            class: "form-text text-muted"
                        }, vnode.attrs.instruction) : null,
                ]
            )
        }
    }
}

export function RadioGroup(): m.Component<RadioGroupAttrs> {
    let validationReporting: ValidationReporting = {
        visible: false,
        message: null
    };

    return {
        oncreate(vnode) {
            if (vnode.attrs.initialValue) {
                let fieldElem = vnode.dom.querySelector(`input[value="${vnode.attrs.initialValue}"]`);
                if (fieldElem) {
                    fieldElem.setAttribute('checked', "true");
                }
            }
        },
        view(vnode) {
            let isRequired = vnode.attrs.required || false;
            return m("div.form-group", { style: "position: relative;" }, [
                m(Label, {
                    label: vnode.attrs.label,
                    required: true
                }),

                m("div.form-check--", {
                    class: vnode.attrs.useBackground == true ? "px-3 py-2 bg-white rounded" : ""
                },
                    vnode.attrs.options.map(option =>
                        m("div", {
                            class: vnode.attrs.layout == 'vertical' ?
                                "custom-control custom-radio py-2" :
                                "custom-control custom-control-inline custom-radio py-1",
                            /* onclick: () => {
                                vnode.attrs.onValueChange(option.value);

                                if (vnode.attrs.getCustomValidityMessage) {
                                    validationReporting.visible = false;
                                }
                            } */
                        }, [
                            m("input", {
                                name: vnode.attrs.name,
                                value: option.value,
                                type: "radio",
                                class: "custom-control-input pr-2",
                                id: option.value,
                                required: isRequired,
                                onclick: (event) => {
                                    console.log(event.target.value);
                                    vnode.attrs.onValueChange(event.target.value);

                                    if (vnode.attrs.getCustomValidityMessage) {
                                        /*let elems = document.querySelectorAll(`input[name="${vnode.attrs.name}"]`);
                                        elems.forEach(elem => {
                                            (elem as any).setCustomValidity('');
                                        });*/
                                        validationReporting.visible = false;
                                    }
                                },
                                oninvalid: (event) => {
                                    if (event.target && vnode.attrs.getCustomValidityMessage) {
                                        let message = vnode.attrs.getCustomValidityMessage(event.target.validity);
                                        //event.target.setCustomValidity(message);      
                                        validationReporting.visible = true;
                                        validationReporting.message = message;
                                        vnode.attrs.onInvalid(event.target.validity);
                                    }
                                },
                                onchange: (event) => {
                                    if (vnode.attrs.getCustomValidityMessage) {
                                        //event.target.setCustomValidity('');
                                        validationReporting.visible = false;
                                    }
                                },
                            }),
                            m("label", { 
                                class: "custom-control-label", 
                                style: vnode.attrs.layout == 'vertical' ? "min-width: 75%" : "",
                                for: option.value },
                                option.label,
                                (option.description) ? m("span.ml-2 me-2", option.description) : null
                            )
                        ])
                    )
                ),
                (vnode.attrs.validityReportingAllowed && validationReporting.visible) ?
                    m("div.validation-popup",
                        m("span.validation-message",
                            m("span.text-secondary", (vnode.attrs.lang == 'en') ? validationReporting.message?.en : validationReporting.message?.my)
                        )
                    ) : null,
            ])
        }
    }
}


